'use client';

import { ProfileImage } from '@/components';
import { useUserQuery } from '@/entities/auth';
import { useLoginInducingModal } from '@/hooks/useLoginInducingModal';
import { qaAttr } from '@/lib/qa';
import { IconButton, Row, Textarea } from '@vuddy/components';
import { SECOND } from '@vuddy/utils';
import { isEmpty, isNil } from 'lodash-es';
import { FocusEvent, useEffect, useRef, useState } from 'react';
import * as styles from './commentInput.css';

interface SalePageCommentInputProps {
  initialValue?: string;
  interactive?: boolean;
  autoFocus?: boolean;
  commentLabel?: string;
  onEnterKeyDown?: (value: string, clearValue: () => void) => void;
  onBlur?: VoidFunction;
  onFocus?: VoidFunction;
  onInputStart?: VoidFunction;
  qaId?: string;
}

export const SalePageCommentInput = ({
  initialValue = '',
  interactive = true,
  autoFocus,
  commentLabel = '댓글',
  onEnterKeyDown,
  onBlur,
  onFocus,
  onInputStart,
  qaId,
}: SalePageCommentInputProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const coolDownTimer = useRef<NodeJS.Timeout | null>(null);
  const loginConfirm = useLoginInducingModal();
  const [value, setValue] = useState(initialValue);
  const userQuery = useUserQuery();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleFocus = (e: FocusEvent<HTMLTextAreaElement>) => {
    // 쿼리가 성공했고 데이터가 있는 경우에만 포커스 허용
    if (!userQuery.isSuccess || isNil(userQuery.data)) {
      e.target.blur();
    } else {
      onFocus?.();
    }
  };

  const startCoolDownTimer = () => {
    coolDownTimer.current = setTimeout(() => {
      coolDownTimer.current = null;
    }, 2 * SECOND);
  };

  const clearValue = () => setValue('');

  const handleSendButtonClick = () => {
    if (isEmpty(value)) {
      onInputStart?.();
      return;
    }
    if (isNil(coolDownTimer.current)) {
      startCoolDownTimer();
      onEnterKeyDown?.(value, clearValue);
    }
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const wrapper = ref.current;

      if (isNil(wrapper)) return;

      // 외부 클릭 시 onBlur 호출
      if (!wrapper.contains(e.target as HTMLElement)) {
        onBlur?.();
        return;
      }

      // 내부 클릭 시 로그인 체크 - 쿼리가 성공하지 않았거나 데이터가 없는 경우
      if (!userQuery.isSuccess || isNil(userQuery.data)) {
        loginConfirm();
      }
    };

    window.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('click', handleClick);
    };
  }, [onBlur, userQuery.isSuccess, userQuery.data, loginConfirm]);

  return (
    <div ref={ref} {...(qaId ? qaAttr(qaId) : {})}>
      <Row
        className={!interactive ? styles.preventInteractive : undefined}
        gap="gap_base"
        alignItems="center"
        paddingY="padding_2xs">
        <ProfileImage url={userQuery.data?.profileImage?.url} imageSize="xs" />
        <Row width="100%" alignItems="center" position="relative">
          <Row
            width="100%"
            paddingY="padding_xs"
            paddingLeft="padding_xs"
            borderRadius="radius_2xl"
            backgroundColor="tertiary"
            alignItems="center">
            <Textarea
              className={styles.textarea}
              ref={textareaRef}
              placeholder={`${commentLabel}을 입력해주세요.`}
              onFocus={handleFocus}
              value={value}
              onChange={e => {
                onInputStart?.();
                setValue(e.target.value.trimStart());
              }}
              disabled={!userQuery.isSuccess}
              onEnter={() => handleSendButtonClick()}
              autoFocus={autoFocus}
              maxLines={3}
            />
          </Row>
          <div className={styles.iconWrapper}>
            <IconButton
              className={styles.sendButton}
              icon={{
                name: 'FILL_SEND',
                size: '20px',
                color: isEmpty(value) ? 'placeholder' : 'secondary',
              }}
              onClick={handleSendButtonClick}
            />
          </div>
        </Row>
      </Row>
    </div>
  );
};
